<template>
    <div data-testid="development-goal-update">
        <DevelopmentGoalCard :goal="goal" :previous-target-date="showsDateChange ? proposal.current_target_date : null" />
        <MarkdownText
            class="body mx-2 -mt-2 mb-4"
            :class="isSaving || (!isActive && !savedGoal) ? 'text-ink-muted' : 'text-ink'"
            :text="note"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-testid="development-goal-update-note" />
    </div>
</template>

<script setup lang="ts">
import type { DevelopmentGoal } from "~vue/api/generated/development-journeys";
import DevelopmentGoalCard from "~vue/ChatDetail/DevelopmentGoalCard.vue";
import { developmentGoalUpdateQuestion } from "~vue/ChatDetail/developmentGoalUpdate";
import MarkdownText from "~vue/MarkdownText.vue";
import type { DevelopmentGoalUpdateProposal } from "~vue/types/chat";
import { formatDate } from "~vue/utils/formatDate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const {
    proposal,
    savedGoal = null,
    isActive = false,
    isSaving = false,
} = defineProps<{
    proposal: DevelopmentGoalUpdateProposal;
    savedGoal?: Pick<DevelopmentGoal, "status" | "target_date"> | null;
    isActive?: boolean;
    isSaving?: boolean;
}>();

const { t } = useI18n();

// A cancelled or superseded date change keeps showing the goal's unchanged date.
const showsDateChange = computed(() => proposal.change === "target_date" && (savedGoal !== null || isActive || isSaving));

const goal = computed(() => ({
    title: proposal.title,
    achievement_criteria: proposal.achievement_criteria,
    target_date: showsDateChange.value ? proposal.target_date : (savedGoal?.target_date ?? proposal.current_target_date),
}));

const newTargetDate = computed(() => formatDate(proposal.target_date ?? "", "date-long"));

// The line under the card asks for confirmation, then says what was saved.
const note = computed(() => {
    if (isSaving) {
        return t("developmentJourneys.goalProgress.goalUpdate.saving");
    }
    if (savedGoal) {
        if (proposal.change === "target_date") {
            return t("developmentJourneys.goalProgress.goalUpdate.saved.targetDate", { date: newTargetDate.value });
        }
        return proposal.change === "achieved"
            ? t("developmentJourneys.goalProgress.goalUpdate.saved.achieved")
            : t("developmentJourneys.goalProgress.goalUpdate.saved.noLongerPursuing");
    }
    if (!isActive) {
        return t("developmentJourneys.goalProgress.goalUpdate.notSaved");
    }
    return developmentGoalUpdateQuestion(t, proposal);
});
</script>
